<script setup>
/* global axios */
import { ref, computed, onMounted, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Doughnut } from 'vue-chartjs';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');

const today = new Date();
const thirtyDaysAgo = new Date(today);
thirtyDaysAgo.setDate(today.getDate() - 30);

const dateFrom = ref(thirtyDaysAgo.toISOString().slice(0, 10));
const dateTo = ref(today.toISOString().slice(0, 10));
const selectedAgent = ref('');
const selectedTeam = ref('');
const isLoading = ref(false);
const reportData = shallowRef(null);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);

const agentOptions = computed(() => [
  { value: '', label: 'Todos os responsáveis' },
  ...agents.value.map(a => ({ value: a.id, label: a.name })),
]);
const teamOptions = computed(() => [
  { value: '', label: 'Todos os times' },
  ...teams.value.map(t => ({ value: t.id, label: t.name })),
]);

const filterParams = computed(() => {
  const p = { date_from: dateFrom.value, date_to: dateTo.value };
  if (selectedAgent.value) p.agent_id = selectedAgent.value;
  if (selectedTeam.value) p.team_id = selectedTeam.value;
  return p;
});

async function fetchReports() {
  isLoading.value = true;
  try {
    const { data } = await axios.get(
      `/api/v2/accounts/${accountId.value}/conversation_flow_reports`,
      { params: filterParams.value }
    );
    reportData.value = data;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Conversation Flow Reports]', e);
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  fetchReports();
});

// ─── Summary ────────────────────────────────────────────
const summary = computed(() => reportData.value?.summary || {});

const summaryCards = computed(() => [
  {
    label: 'Entraram no Período',
    value: summary.value.incoming || 0,
    icon: 'i-lucide-inbox',
    color: '#6366f1',
    bg: 'rgba(99, 102, 241, 0.08)',
  },
  {
    label: 'Abertos',
    value: summary.value.open || 0,
    icon: 'i-lucide-message-circle',
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.08)',
  },
  {
    label: 'Resolvidos no Período',
    value: summary.value.resolved || 0,
    icon: 'i-lucide-check-circle-2',
    color: '#22c55e',
    bg: 'rgba(34, 197, 94, 0.08)',
  },
  {
    label: 'Pendentes',
    value: summary.value.pending || 0,
    icon: 'i-lucide-clock',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.08)',
  },
  {
    label: 'Em Soneca',
    value: summary.value.snoozed || 0,
    icon: 'i-lucide-alarm-clock',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.08)',
  },
  {
    label: 'Sem Responsável',
    value: summary.value.unassigned || 0,
    icon: 'i-lucide-user-x',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.08)',
  },
]);

// ─── Chart Options ──────────────────────────────────────
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { padding: 16, usePointStyle: true, pointStyle: 'circle' },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { maxRotation: 45, font: { size: 11 } },
    },
    y: {
      grid: { color: 'rgba(0,0,0,0.04)' },
      border: { display: false },
      beginAtZero: true,
    },
  },
};

const horizontalOptions = {
  ...chartOptions,
  indexAxis: 'y',
  scales: {
    x: {
      grid: { color: 'rgba(0,0,0,0.04)' },
      border: { display: false },
      beginAtZero: true,
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 12 } },
    },
  },
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: { padding: 16, usePointStyle: true, pointStyle: 'circle' },
    },
  },
};

// ─── Chart Data ─────────────────────────────────────────
const dailyTimeline = computed(() => reportData.value?.daily_timeline || []);

const timelineChartData = computed(() => ({
  labels: dailyTimeline.value.map(d =>
    new Date(d.date + 'T12:00:00').toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    })
  ),
  datasets: [
    {
      label: 'Entraram',
      data: dailyTimeline.value.map(d => d.incoming),
      backgroundColor: 'rgba(99, 102, 241, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Abertos',
      data: dailyTimeline.value.map(d => d.open),
      backgroundColor: 'rgba(59, 130, 246, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Resolvidos',
      data: dailyTimeline.value.map(d => d.resolved),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderRadius: 4,
    },
  ],
}));

const statusDoughnut = computed(() => ({
  labels: ['Abertos', 'Resolvidos', 'Pendentes', 'Em Soneca'],
  datasets: [
    {
      data: [
        summary.value.open || 0,
        summary.value.resolved_current || 0,
        summary.value.pending || 0,
        summary.value.snoozed || 0,
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(34, 197, 94, 0.8)',
        'rgba(245, 158, 11, 0.8)',
        'rgba(139, 92, 246, 0.8)',
      ],
      borderWidth: 0,
    },
  ],
}));

const byAgent = computed(() => reportData.value?.by_agent || []);

const agentChartData = computed(() => ({
  labels: byAgent.value.map(a => a.name),
  datasets: [
    {
      label: 'Entraram',
      data: byAgent.value.map(a => a.incoming),
      backgroundColor: 'rgba(99, 102, 241, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Abertos',
      data: byAgent.value.map(a => a.open),
      backgroundColor: 'rgba(59, 130, 246, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Resolvidos',
      data: byAgent.value.map(a => a.resolved),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderRadius: 4,
    },
  ],
}));

const byTeam = computed(() => reportData.value?.by_team || []);

const teamChartData = computed(() => ({
  labels: byTeam.value.map(t => t.name),
  datasets: [
    {
      label: 'Entraram',
      data: byTeam.value.map(t => t.incoming),
      backgroundColor: 'rgba(99, 102, 241, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Abertos',
      data: byTeam.value.map(t => t.open),
      backgroundColor: 'rgba(59, 130, 246, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Resolvidos',
      data: byTeam.value.map(t => t.resolved),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderRadius: 4,
    },
  ],
}));

function resolutionRate(item) {
  if (!item.incoming || item.incoming === 0) return '0%';
  return `${Math.round((item.resolved / item.incoming) * 100)}%`;
}

// ─── Agent Activity ─────────────────────────────────────
const agentActivity = computed(() => reportData.value?.agent_activity || []);

const activityChartData = computed(() => ({
  labels: agentActivity.value.map(a => a.name),
  datasets: [
    {
      label: 'Atribuídas Atualmente',
      data: agentActivity.value.map(a => a.currently_assigned),
      backgroundColor: 'rgba(239, 68, 68, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Conversas Participadas',
      data: agentActivity.value.map(a => a.conversations_participated),
      backgroundColor: 'rgba(16, 185, 129, 0.7)',
      borderRadius: 4,
    },
    {
      label: 'Resolvidas',
      data: agentActivity.value.map(a => a.conversations_resolved),
      backgroundColor: 'rgba(34, 197, 94, 0.7)',
      borderRadius: 4,
    },
  ],
}));
</script>

<template>
  <div class="flex-1 overflow-auto">
    <div class="max-w-7xl mx-auto px-6 py-8">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-n-slate-12">
          📞 Relatório de Atendimentos
        </h1>
        <p class="text-sm text-n-slate-10 mt-1">
          Acompanhe o fluxo de atendimentos: entradas, abertos e
          resolvidos no período.
        </p>
      </div>

      <!-- Filters -->
      <div
        class="flex flex-wrap items-end gap-3 mb-8 p-4 bg-n-solid-2 rounded-xl border border-n-weak"
      >
        <NextInput v-model="dateFrom" type="date" label="De" size="sm" />
        <NextInput v-model="dateTo" type="date" label="Até" size="sm" />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            Responsável
          </label>
          <NextSelect v-model="selectedAgent" :options="agentOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">Time</label>
          <NextSelect v-model="selectedTeam" :options="teamOptions" />
        </div>
        <NextButton
          label="Filtrar"
          icon="i-lucide-search"
          size="sm"
          :is-loading="isLoading"
          @click="fetchReports"
        />
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <span class="i-lucide-loader-2 animate-spin size-8 text-n-brand" />
      </div>

      <template v-else-if="reportData">
        <!-- Summary Cards -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div
            v-for="card in summaryCards"
            :key="card.label"
            class="rounded-xl border border-n-weak p-4 transition-shadow hover:shadow-md"
            :style="{ backgroundColor: card.bg }"
          >
            <div class="flex items-center gap-2 mb-2">
              <span :class="card.icon" class="size-4" :style="{ color: card.color }" />
              <span class="text-xs font-semibold uppercase tracking-wider text-n-slate-10">
                {{ card.label }}
              </span>
            </div>
            <span class="text-2xl font-bold" :style="{ color: card.color }">
              {{ card.value.toLocaleString('pt-BR') }}
            </span>
          </div>
        </div>

        <!-- Charts Row 1 -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div
            class="lg:col-span-2 bg-n-solid-2 rounded-xl border border-n-weak p-5"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
              <span
                class="i-lucide-bar-chart-3 inline-block size-4 align-middle mr-1"
                style="color: #6366f1"
              />
              Evolução Diária
            </h3>
            <div style="height: 300px">
              <Bar
                v-if="dailyTimeline.length > 0"
                :data="timelineChartData"
                :options="chartOptions"
              />
              <div
                v-else
                class="flex items-center justify-center h-full text-sm text-n-slate-10"
              >
                Sem dados no período
              </div>
            </div>
          </div>
          <div class="bg-n-solid-2 rounded-xl border border-n-weak p-5">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
              <span
                class="i-lucide-pie-chart inline-block size-4 align-middle mr-1"
                style="color: #22c55e"
              />
              Status Atual
            </h3>
            <div style="height: 300px">
              <Doughnut :data="statusDoughnut" :options="doughnutOptions" />
            </div>
          </div>
        </div>

        <!-- Charts Row 2 -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div class="bg-n-solid-2 rounded-xl border border-n-weak p-5">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
              <span
                class="i-lucide-users inline-block size-4 align-middle mr-1"
                style="color: #8b5cf6"
              />
              Por Responsável
            </h3>
            <div :style="{ height: Math.max(200, byAgent.length * 40) + 'px' }">
              <Bar
                v-if="byAgent.length > 0"
                :data="agentChartData"
                :options="horizontalOptions"
              />
              <div
                v-else
                class="flex items-center justify-center h-full text-sm text-n-slate-10"
              >
                Sem dados
              </div>
            </div>
          </div>
          <div class="bg-n-solid-2 rounded-xl border border-n-weak p-5">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
              <span
                class="i-lucide-building-2 inline-block size-4 align-middle mr-1"
                style="color: #06b6d4"
              />
              Por Time
            </h3>
            <div :style="{ height: Math.max(200, byTeam.length * 40) + 'px' }">
              <Bar
                v-if="byTeam.length > 0"
                :data="teamChartData"
                :options="horizontalOptions"
              />
              <div
                v-else
                class="flex items-center justify-center h-full text-sm text-n-slate-10"
              >
                Sem dados
              </div>
            </div>
          </div>
        </div>

        <!-- Agent Ranking Table -->
        <div
          v-if="byAgent.length > 0"
          class="bg-n-solid-2 rounded-xl border border-n-weak p-5 mb-8"
        >
          <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
            <span
              class="i-lucide-trophy inline-block size-4 align-middle mr-1"
              style="color: #f59e0b"
            />
            Ranking de Responsáveis
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-n-weak text-left text-xs text-n-slate-10 uppercase tracking-wider"
                >
                  <th class="py-2 pr-4">#</th>
                  <th class="py-2 pr-4">Responsável</th>
                  <th class="py-2 pr-4 text-right">Entraram</th>
                  <th class="py-2 pr-4 text-right">Abertos</th>
                  <th class="py-2 pr-4 text-right">Resolvidos</th>
                  <th class="py-2 text-right">% Resolução</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(agent, idx) in byAgent"
                  :key="agent.id"
                  class="border-b border-n-weak/50"
                >
                  <td class="py-2.5 pr-4 text-n-slate-10 font-medium">
                    {{ idx + 1 }}
                  </td>
                  <td class="py-2.5 pr-4">
                    <div class="flex items-center gap-2">
                      <span
                        class="size-6 rounded-full bg-n-alpha-2 flex items-center justify-center text-[10px] font-bold text-n-slate-11"
                      >
                        {{ agent.name?.charAt(0)?.toUpperCase() }}
                      </span>
                      <span class="text-n-slate-12 font-medium">
                        {{ agent.name }}
                      </span>
                    </div>
                  </td>
                  <td class="py-2.5 pr-4 text-right text-n-slate-12">
                    {{ agent.incoming }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-blue-600">
                    {{ agent.open }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-green-600">
                    {{ agent.resolved }}
                  </td>
                  <td class="py-2.5 text-right font-semibold text-n-slate-12">
                    {{ resolutionRate(agent) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Team Ranking Table -->
        <div
          v-if="byTeam.length > 0"
          class="bg-n-solid-2 rounded-xl border border-n-weak p-5 mb-8"
        >
          <h3 class="text-sm font-semibold text-n-slate-12 mb-4">
            <span
              class="i-lucide-building-2 inline-block size-4 align-middle mr-1"
              style="color: #06b6d4"
            />
            Ranking de Times
          </h3>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-n-weak text-left text-xs text-n-slate-10 uppercase tracking-wider"
                >
                  <th class="py-2 pr-4">#</th>
                  <th class="py-2 pr-4">Time</th>
                  <th class="py-2 pr-4 text-right">Entraram</th>
                  <th class="py-2 pr-4 text-right">Abertos</th>
                  <th class="py-2 pr-4 text-right">Resolvidos</th>
                  <th class="py-2 text-right">% Resolução</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(team, idx) in byTeam"
                  :key="team.id"
                  class="border-b border-n-weak/50"
                >
                  <td class="py-2.5 pr-4 text-n-slate-10 font-medium">
                    {{ idx + 1 }}
                  </td>
                  <td class="py-2.5 pr-4 text-n-slate-12 font-medium">
                    {{ team.name }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-n-slate-12">
                    {{ team.incoming }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-blue-600">
                    {{ team.open }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-green-600">
                    {{ team.resolved }}
                  </td>
                  <td class="py-2.5 text-right font-semibold text-n-slate-12">
                    {{ resolutionRate(team) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- Agent Activity Section -->
        <div
          v-if="agentActivity.length > 0"
          class="bg-n-solid-2 rounded-xl border border-n-weak p-5 mb-8"
        >
          <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
            <span
              class="i-lucide-activity inline-block size-4 align-middle mr-1"
              style="color: #10b981"
            />
            Atividade Real do Agente
          </h3>
          <p class="text-xs text-n-slate-10 mb-4">
            Mostra conversas em que o agente efetivamente enviou mensagens,
            independente de estar atribuído.
          </p>

          <!-- Comparison Chart -->
          <div
            :style="{
              height: Math.max(200, agentActivity.length * 44) + 'px',
            }"
            class="mb-6"
          >
            <Bar :data="activityChartData" :options="horizontalOptions" />
          </div>

          <!-- Activity Table -->
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-n-weak text-left text-xs text-n-slate-10 uppercase tracking-wider"
                >
                  <th class="py-2 pr-4">#</th>
                  <th class="py-2 pr-4">Agente</th>
                  <th class="py-2 pr-4 text-right">Participou</th>
                  <th class="py-2 pr-4 text-right">Msgs Enviadas</th>
                  <th class="py-2 pr-4 text-right">Resolvidas</th>
                  <th class="py-2 pr-4 text-right">Abertas</th>
                  <th class="py-2 text-right">Atribuídas Agora</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(agent, idx) in agentActivity"
                  :key="agent.id"
                  class="border-b border-n-weak/50"
                >
                  <td class="py-2.5 pr-4 text-n-slate-10 font-medium">
                    {{ idx + 1 }}
                  </td>
                  <td class="py-2.5 pr-4">
                    <div class="flex items-center gap-2">
                      <span
                        class="size-6 rounded-full bg-n-alpha-2 flex items-center justify-center text-[10px] font-bold text-n-slate-11"
                      >
                        {{ agent.name?.charAt(0)?.toUpperCase() }}
                      </span>
                      <span class="text-n-slate-12 font-medium">
                        {{ agent.name }}
                      </span>
                    </div>
                  </td>
                  <td
                    class="py-2.5 pr-4 text-right font-semibold text-emerald-600"
                  >
                    {{ agent.conversations_participated }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-n-slate-12">
                    {{ agent.messages_sent.toLocaleString('pt-BR') }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-green-600">
                    {{ agent.conversations_resolved }}
                  </td>
                  <td class="py-2.5 pr-4 text-right text-blue-600">
                    {{ agent.conversations_opened }}
                  </td>
                  <td class="py-2.5 text-right text-n-slate-10">
                    {{ agent.currently_assigned }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
